import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MediaCard from "@/components/base/MediaCard";
import { mediaList } from "@/mocks/media";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const q = (searchParams.get("q") || "").trim();
  const [input, setInput] = useState(q);

  useDocumentTitle(
    q ? `搜索“${q}” - 光棍影院` : "搜索影视 - 光棍影院",
    "在光棍影院搜索电影与电视剧，支持按片名、主演、导演关键词快速查找想看的内容。"
  );

  const results = useMemo(() => {
    if (!q) return [];
    const key = q.toLowerCase();
    return mediaList.filter((item) => {
      const haystack = [
        item.title,
        item.director,
        ...item.cast,
        ...item.genre,
        item.region,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(key);
    });
  }, [q]);

  const hot = useMemo(() => [...mediaList].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const keyword = input.trim();
    navigate(keyword ? `/search?q=${encodeURIComponent(keyword)}` : "/search");
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />
      <main className="flex-1 w-full px-4 md:px-8 pt-24 md:pt-28 pb-14">
        <nav className="text-sm text-foreground-500 mb-4 flex items-center gap-1.5">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 font-medium">搜索</span>
        </nav>

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
          搜索影视作品
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500">
          输入片名、主演或导演关键词，快速找到想看的内容
        </p>

        <form
          onSubmit={handleSubmit}
          className="mt-5 flex flex-col sm:flex-row items-stretch gap-3 max-w-2xl"
        >
          <div className="flex items-center gap-2 flex-1 rounded-md px-4 py-3 bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors">
            <i className="ri-search-line text-lg text-foreground-500"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="例如：长安夜行录、悬疑、沈知远"
              className="w-full bg-transparent text-sm outline-none text-foreground-900 placeholder-foreground-400"
            />
            {input && (
              <button
                type="button"
                aria-label="清空"
                onClick={() => setInput("")}
                className="text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-circle-fill text-lg"></i>
              </button>
            )}
          </div>
          <button
            type="submit"
            className="px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {q && (
          <p className="mt-6 text-sm text-foreground-600">
            关键词「<span className="font-semibold text-accent-600">{q}</span>」共找到{" "}
            <span className="font-semibold text-accent-600">{results.length}</span> 部相关作品
          </p>
        )}

        {q && results.length > 0 && (
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {results.map((item) => (
              <MediaCard key={item.id} item={item} />
            ))}
          </div>
        )}

        {q && results.length === 0 && (
          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 py-14 flex flex-col items-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-200 flex items-center justify-center mb-4">
              <i className="ri-search-eye-line text-3xl text-foreground-400"></i>
            </span>
            <p className="text-base font-semibold text-foreground-800">未找到相关影视作品</p>
            <p className="text-sm text-foreground-500 mt-1">
              换个关键词试试，或看看下面的热门推荐
            </p>
            <div className="mt-8 w-full max-w-4xl px-4">
              <h2 className="text-sm font-semibold text-foreground-700 mb-4 text-left flex items-center gap-2">
                <i className="ri-fire-line text-accent-500"></i>
                热门推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
                {hot.map((item) => (
                  <MediaCard key={item.id} item={item} compact />
                ))}
              </div>
            </div>
          </div>
        )}

        {!q && (
          <div className="mt-10">
            <h2 className="text-lg font-bold text-foreground-950 font-heading flex items-center gap-2 mb-5">
              <i className="ri-fire-line text-accent-500"></i>
              大家都在看
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {hot.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}